// App Success Stories - Real project results and case studies
import React, { useState, useEffect } from 'react';
import { TrendingUp, Clock, Smartphone, DollarSign, Users, Zap, Brain, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

const successStories = [{
  id: 'propertyagent-crm',
  title: 'PropertyAgent-CRM',
  subtitle: 'AI-Powered Real Estate CRM for Professional Agents',
  description: 'The best CRM for real estate agents with advanced lead management, deal tracking, and commission forecasting. Built for solo agents and real estate professionals.',
  timeline: '12 weeks',
  category: 'Web Application + PWA',
  results: [{
    metric: '+30%',
    label: 'Closing rate increase',
    icon: TrendingUp
  }, {
    metric: 'Mobile-First',
    label: 'PWA for field work',
    icon: Smartphone
  }, {
    metric: 'AI Copilot',
    label: 'Pipeline risk alerts',
    icon: Brain
  }, {
    metric: 'Visual',
    label: 'Kanban deal pipeline',
    icon: CheckCircle
  }],
  techStack: ['React', 'TypeScript', 'Supabase', 'Tailwind CSS'],
  challenge: 'Real estate agents needed a CRM specifically designed for their workflow, not a generic system adapted for real estate. They needed mobile access for field work, visual pipeline management, and AI powered insights.',
  solution: 'Built a mobile-first PWA with AI-powered pipeline analysis, visual Kanban-style deal tracking, commission forecasting, and real-time analytics designed by real estate professionals.',
  keyFeatures: ['AI-powered pipeline analysis with at-risk deal alerts', 'Visual Kanban deal pipeline with drag-and-drop', 'Real-time commission forecasting and revenue tracking', 'Mobile-first PWA architecture for field work', 'Complete lead-to-closing workflow management'],
  image: '/images/portfolio/propertyagent-dashboard.png',
  screenshots: [
    '/images/portfolio/propertyagent-hero.png',
    '/images/portfolio/propertyagent-dashboard.png',
    '/images/portfolio/propertyagent-pipeline.png'
  ],
  logo: '/images/portfolio/propertyagent-logo.png'
}, {
  id: 'selgr',
  title: 'SELGR',
  subtitle: 'AI-Powered Sales Training for Norwegian B2B Salespeople',
  description: 'A practical digital training platform that helps salespeople become more confident and effective in customer meetings through realistic AI roleplay, personalized feedback, and structured course modules.',
  timeline: '16 weeks',
  category: 'SaaS Platform',
  results: [{
    metric: 'AI Roleplay',
    label: 'Realistic customer simulations',
    icon: Brain
  }, {
    metric: 'Personalized',
    label: 'Adapts to your product/company',
    icon: Users
  }, {
    metric: '10+ Modules',
    label: 'Structured course content',
    icon: CheckCircle
  }, {
    metric: 'Real-time',
    label: 'Live coaching feedback',
    icon: Zap
  }],
  techStack: ['React', 'Node.js', 'PostgreSQL', 'OpenAI', 'Gemini', 'Auth0', 'Stripe'],
  challenge: 'Norwegian B2B salespeople needed practical, hands on training, not theoretical courses. They required a way to practice customer conversations safely, get instant feedback, and track their progress over time.',
  solution: 'Built a complete sales training SaaS with AI-powered roleplay simulations that adapt to each user\'s company, product, and skill level. Includes structured course modules with quizzes, practical daily tasks, progression tracking with badges, and real-time coaching during practice sessions.',
  keyFeatures: ['AI roleplay with realistic customer personas and objection handling', 'Live coaching and feedback during practice conversations', 'Course modules with lessons, examples, and interactive quizzes', 'Customization to user\'s specific product, service, and company', 'Progression tracking with XP rewards and achievement badges', 'Multiple difficulty levels from beginner to advanced'],
  image: '/images/portfolio/selgr-roleplay.png',
  screenshots: [
    '/images/portfolio/selgr-hero.png',
    '/images/portfolio/selgr-course.png',
    '/images/portfolio/selgr-roleplay.png'
  ],
  logo: '/images/portfolio/selgr-logo.png'
}];

export const AppSuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const { elementRef, getRevealStyle } = useScrollReveal();
  
  const currentStory = successStories[activeStory];
  const screenshots = currentStory.screenshots || [currentStory.image];
  
  // Reset screenshot index when switching stories
  const handleStoryChange = (index: number) => {
    setActiveStory(index);
    setActiveScreenshot(0);
    setProgress(0);
  };
  
  // Auto-play with progress tracking
  useEffect(() => {
    if (isPaused || screenshots.length <= 1) {
      return;
    }
    
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveScreenshot((current) => (current + 1) % screenshots.length);
          return 0;
        }
        return prev + 2; // 2% every 80ms = 4 seconds total
      });
    }, 80);
    
    return () => clearInterval(progressInterval);
  }, [isPaused, screenshots.length, activeStory]);
  
  // Reset progress when manually changing screenshot
  const goToScreenshot = (index: number) => {
    setActiveScreenshot(index);
    setProgress(0);
  };
  
  const nextScreenshot = () => {
    setActiveScreenshot((prev) => (prev + 1) % screenshots.length);
    setProgress(0);
  };
  
  const prevScreenshot = () => {
    setActiveScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    setProgress(0);
  };

  return (
    <section className="section">
      <div ref={elementRef} className="container-professional">
        {/* Header */}
        <div className="section-header-professional">
          <div className="micro-label-professional">SUCCESS STORIES</div>
          <h2 className="heading-section">
            Real projects, measurable results
          </h2>
          <p className="text-body max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Discover how we've helped businesses streamline operations and achieve growth goals
          </p>
        </div>

        {/* Story navigation tabs - Premium design */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 max-w-4xl mx-auto">
          {successStories.map((story, index) => (
            <button
              key={story.id}
              onClick={() => handleStoryChange(index)}
              className={`
                relative px-6 py-4 rounded-xl text-left transition-all duration-300
                border backdrop-blur-sm
                ${activeStory === index 
                  ? 'bg-gradient-to-br from-primary/20 to-primary/5 border-primary/50 shadow-lg shadow-primary/10' 
                  : 'bg-[#0d0d0d] border-white/10 hover:border-white/20 hover:bg-[#111111]'
                }
              `}
            >
              <div className="flex items-center gap-3">
                {story.logo && (
                  <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
                    <img src={story.logo} alt="" className="h-6 w-auto" />
                  </div>
                )}
                <div>
                  <div className={`font-semibold text-sm ${activeStory === index ? 'text-white' : 'text-white/80'}`}>
                    {story.title}
                  </div>
                  <div className={`text-xs mt-0.5 ${activeStory === index ? 'text-primary' : 'text-white/50'}`}>
                    {story.category}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Active story content */}
        <div className="bg-[#111111] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto" style={getRevealStyle(1)}>
          {/* Logo and Title */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              {currentStory.logo && (
                <div className="bg-white rounded-lg p-3 flex-shrink-0">
                  <img src={currentStory.logo} alt={`${currentStory.title} logo`} className="h-8 w-auto" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary">
                  {currentStory.category}
                </span>
                <span className="flex items-center gap-2 text-xs text-white/60">
                  <Clock className="w-3.5 h-3.5" />
                  {currentStory.timeline}
                </span>
              </div>
            </div>
            
            <h3 className="text-2xl font-semibold text-white mb-3 leading-tight">
              {currentStory.title}
            </h3>
            
            <p className="text-base leading-relaxed text-white/70">
              {currentStory.description}
            </p>
          </div>

          {/* Screenshot Carousel - Premium Browser Frame */}
          {screenshots.length > 0 && (
            <div className="mb-10 -mx-8 md:-mx-12">
              <div 
                className="relative group"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                {/* Browser Frame Container */}
                <div className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-white/10 shadow-2xl transition-all duration-500 hover:shadow-primary/5">
                  {/* Browser Bar */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-[#0d0d0d] border-b border-white/5">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                      <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                      <div className="w-3 h-3 rounded-full bg-[#28ca42]" />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-[#0a0a0a] rounded-md px-4 py-1.5 text-xs text-white/40 text-center max-w-xs mx-auto border border-white/5">
                        {currentStory.title.toLowerCase().replace(/\s+/g, '')}.app
                      </div>
                    </div>
                    <div className="w-16" /> {/* Spacer for symmetry */}
                  </div>
                  
                  {/* Screenshot Image */}
                  <div className="relative overflow-hidden">
                    <img 
                      key={`${activeStory}-${activeScreenshot}`}
                      src={screenshots[activeScreenshot]} 
                      alt={`${currentStory.title} interface ${activeScreenshot + 1}`} 
                      className="w-full h-auto animate-fade-in" 
                    />
                  </div>
                </div>
                
                {/* Navigation Arrows - Premium Style */}
                {screenshots.length > 1 && (
                  <>
                    <button
                      onClick={prevScreenshot}
                      className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full 
                        bg-gradient-to-r from-[#0a0a0a] to-[#0a0a0a]/80 
                        backdrop-blur-md border border-white/20 
                        flex items-center justify-center 
                        opacity-0 group-hover:opacity-100 
                        hover:border-primary hover:scale-110 hover:shadow-lg hover:shadow-primary/20
                        transition-all duration-300"
                      aria-label="Previous screenshot"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={nextScreenshot}
                      className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full 
                        bg-gradient-to-r from-[#0a0a0a]/80 to-[#0a0a0a] 
                        backdrop-blur-md border border-white/20 
                        flex items-center justify-center 
                        opacity-0 group-hover:opacity-100 
                        hover:border-primary hover:scale-110 hover:shadow-lg hover:shadow-primary/20
                        transition-all duration-300"
                      aria-label="Next screenshot"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Premium Progress Bar Indicators */}
              {screenshots.length > 1 && (
                <div className="flex gap-2 mt-6 px-8 md:px-12">
                  {screenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToScreenshot(index)}
                      className="h-1 flex-1 rounded-full overflow-hidden bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label={`View screenshot ${index + 1}`}
                    >
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-100 ease-linear"
                        style={{
                          width: activeScreenshot === index 
                            ? `${progress}%` 
                            : activeScreenshot > index 
                              ? '100%' 
                              : '0%'
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Results metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {currentStory.results.map((result, index) => {
              const IconComponent = result.icon;
              return (
                <div key={index} className="text-center p-4 bg-[#0a0a0a] rounded-xl border border-white/8 hover:border-white/15 transition-colors">
                  <IconComponent className="w-6 h-6 mx-auto mb-2 text-primary" strokeWidth={1.5} />
                  <div className="text-xl font-bold text-white mb-1">{result.metric}</div>
                  <div className="text-xs text-white/60">{result.label}</div>
                </div>
              );
            })}
          </div>

          {/* Project details */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">The Challenge</h4>
              <p className="text-sm leading-relaxed text-white/70">
                {currentStory.challenge}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Our Solution</h4>
              <p className="text-sm leading-relaxed text-white/70">
                {currentStory.solution}
              </p>
            </div>
          </div>

          {/* Key Features */}
          {currentStory.keyFeatures && (
            <div className="mb-8">
              <h4 className="text-sm font-semibold text-white mb-4">Key Features Delivered</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {currentStory.keyFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-[#0a0a0a] rounded-lg border border-white/8">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" strokeWidth={1.5} />
                    <span className="text-xs leading-relaxed text-white/70">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tech Stack */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Technology Stack</h4>
            <div className="flex flex-wrap gap-2">
              {currentStory.techStack.map((tech, index) => (
                <span 
                  key={index} 
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-white/5 text-white/70 border-white/8"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
